import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { LoginFormData, LoginErrors } from '@/features/user/types/types';
import { createFormValidator, commonRules } from '@/lib/utils';
import { useLogin } from '@/features/user/hooks/useLogin';
import { SocialSignupButtons } from '@/features/user/components/signup/SocialSignupButtons';
import { FormInput } from '@/features/user/components/signup/FormInput';
import { PasswordInput } from '@/features/user/components/signup/PasswordInput';
import { SubmitButton } from '@/features/user/components/signup/SubmitButton';
import { Toast } from '@/features/user/components/signup/Toast';

interface LoginFormProps {
  onSubmit?: (data: LoginFormData) => Promise<void>;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit }) => {
  const [searchParams] = useSearchParams();
  const { login, isSubmitting } = useLogin();
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<LoginErrors>({});
  const [showToast, setShowToast] = useState(false);

  // Preserve returnUrl when navigating to signup
  const returnUrl = searchParams.get('returnUrl');
  const signupUrl = returnUrl ? `/signup?returnUrl=${encodeURIComponent(returnUrl)}` : '/signup';

  // Create form validator
  const validator = createFormValidator({
    email: commonRules.email('Please enter a valid email'),
    password: commonRules.required('Password is required'),
  });

  const handleInputChange = (field: keyof LoginFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors = validator.validateForm(formData);
    setErrors(newErrors);
    return validator.isFormValid(formData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    try {
      if (onSubmit) {
        await onSubmit(formData);
      } else {
        // Use the login hook for API integration
        await login(formData);
      }
      
      // Show success message
      setShowToast(true);
      
    } catch (error) {
      console.error('Login error:', error);
      setErrors(prev => ({ 
        ...prev, 
        general: 'Login failed. Please check your credentials and try again.' 
      }));
    }
  };

  const handleSocialLogin = (provider: string) => {
    console.log(`Social login with ${provider}`);
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Social Login Buttons */}
        <SocialSignupButtons onSocialSignup={handleSocialLogin} />

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-800"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="px-4 bg-black text-gray-500 text-sm">Or sign in with email</span>
          </div>
        </div>

        {/* Email Field */}
        <FormInput
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(value: string) => handleInputChange('email', value)}
          error={errors.email}
          placeholder="john@example.com"
        />

        {/* Password Field */}
        <PasswordInput
          label="Password"
          value={formData.password}
          onChange={(value: string) => handleInputChange('password', value)}
          error={errors.password}
          placeholder="Enter your password"
        />

        {/* General Error */}
        {errors.general && (
          <div className="text-red-400 text-sm text-center">
            {errors.general}
          </div>
        )}

        {/* Submit Button */}
        <SubmitButton 
          isSubmitting={isSubmitting} 
          text="Sign In" 
          loadingText="Signing In..." 
        />

        {/* Forgot Password Link */}
        <div className="text-center">
          <a 
            href="#" 
            className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
          >
            Forgot your password?
          </a>
        </div>

        {/* Signup Link */}
        <div className="text-center mt-6">
          <p className="text-gray-400 text-sm">
            Don't have an account?{' '}
            <a 
              href={signupUrl} 
              className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
            >
              Create one now
            </a>
          </p>
        </div>
      </form>

      <Toast 
        message="Welcome back!" 
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        duration={3000}
      />
    </>
  );
};
